/**
 * Video API Hooks
 *
 * React Query hooks for video-related API operations
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/services/api/client';
import { queryKeys } from '@/services/query/query-client';
import { config } from '@/config';
import { getAuthTokenAsync } from '@/services/auth/token-manager';

// ============================================================================
// Video Detail Hook
// ============================================================================

export function useVideoDetail(articleId: number, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: [...queryKeys.article(articleId), 'video-detail'],
    queryFn: () => apiClient.getVideoDetail(articleId),
    enabled: enabled && !!articleId,
    staleTime: 10 * 60 * 1000, // 10 minutes - video metadata doesn't change often
    gcTime: 30 * 60 * 1000, // 30 minutes cache time
  });
}

// ============================================================================
// Video Analysis Hook
// ============================================================================

export function useVideoAnalysis(articleId: number, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: [...queryKeys.article(articleId), 'video-analysis'],
    queryFn: () => apiClient.getVideoAnalysis(articleId),
    enabled: enabled && !!articleId,
    staleTime: 15 * 60 * 1000, // 15 minutes - analysis is expensive to generate
    gcTime: 60 * 60 * 1000, // 1 hour cache time
  });
}

// ============================================================================
// Video Transcript Hook
// ============================================================================

export function useVideoTranscript(articleId: number, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: [...queryKeys.article(articleId), 'transcript'],
    queryFn: () => apiClient.getVideoTranscript(articleId),
    enabled: enabled && !!articleId,
    staleTime: 30 * 60 * 1000, // 30 minutes - transcripts don't change
    gcTime: 2 * 60 * 60 * 1000, // 2 hours cache time
  });
}

// ============================================================================
// Video Outline Hook
// ============================================================================

export function useVideoOutline(articleId: number, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: [...queryKeys.article(articleId), 'outline'],
    queryFn: () => apiClient.getVideoOutline(articleId),
    enabled: enabled && !!articleId,
    staleTime: 30 * 60 * 1000, // 30 minutes - outlines don't change
    gcTime: 2 * 60 * 60 * 1000, // 2 hours cache time
  });
}

// ============================================================================
// Video Entities Hook
// ============================================================================

export function useVideoEntities(articleId: number, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: [...queryKeys.article(articleId), 'entities'],
    queryFn: () => apiClient.getVideoEntities(articleId),
    enabled: enabled && !!articleId,
    staleTime: 30 * 60 * 1000, // 30 minutes - entities don't change
    gcTime: 2 * 60 * 60 * 1000, // 2 hours cache time
  });
}

// ============================================================================
// Video Relationships Hook
// ============================================================================

export function useVideoRelationships(articleId: number, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: [...queryKeys.article(articleId), 'relationships'],
    queryFn: () => apiClient.getVideoRelationships(articleId),
    enabled: enabled && !!articleId,
    staleTime: 30 * 60 * 1000, // 30 minutes - relationships don't change
    gcTime: 2 * 60 * 60 * 1000, // 2 hours cache time
  });
}

// ============================================================================
// Combined Video Data Hook
// ============================================================================

export function useVideoData(articleId: number, options: { enabled?: boolean } = {}) {
  const { enabled = true } = options;

  const videoDetail = useVideoDetail(articleId, { enabled });
  const transcript = useVideoTranscript(articleId, { enabled });
  const outline = useVideoOutline(articleId, { enabled });
  const entities = useVideoEntities(articleId, { enabled });
  const relationships = useVideoRelationships(articleId, { enabled });

  return {
    videoDetail,
    transcript,
    outline,
    entities,
    relationships,
    isLoading: videoDetail.isLoading || transcript.isLoading || outline.isLoading || entities.isLoading || relationships.isLoading,
    isError: videoDetail.isError || transcript.isError || outline.isError || entities.isError || relationships.isError,
    error: videoDetail.error || transcript.error || outline.error || entities.error || relationships.error,
  };
}


// ============================================================================
// Utility Functions
// ============================================================================

export function isVideoContent(article: { content_type?: string; youtube_video_id?: string | null }): boolean {
  return article.content_type === 'video' || !!article.youtube_video_id;
}

export function getVideoThumbnailUrl(youtubeVideoId: string, quality: 'default' | 'medium' | 'high' | 'standard' | 'maxres' = 'medium'): string {
  // YouTube thumbnail URL formats:
  // default: 120x90
  // medium: 320x180 (mqdefault.jpg)
  // high: 480x360 (hqdefault.jpg)
  // standard: 640x480 (sddefault.jpg)
  // maxres: 1280x720 (maxresdefault.jpg)

  const qualityMap = {
    'default': 'default.jpg',
    'medium': 'mqdefault.jpg',
    'high': 'hqdefault.jpg',
    'standard': 'sddefault.jpg',
    'maxres': 'maxresdefault.jpg'
  };

  return `https://img.youtube.com/vi/${youtubeVideoId}/${qualityMap[quality]}`;
}

/**
 * Check if an article has a thumbnail image available
 */
export function hasArticleThumbnail(article: any): boolean {
  return !!(article.image_url && article.image_url.trim());
}

/**
 * Get the appropriate thumbnail URL for an article (video or regular article)
 */
export function getArticleThumbnailUrl(article: any): string | null {
  if (isVideoContent(article) && article.youtube_video_id) {
    return getVideoThumbnailUrl(article.youtube_video_id, 'medium');
  }

  if (hasArticleThumbnail(article)) {
    return article.image_url;
  }

  return null;
}

/**
 * Get proxied thumbnail URL for content (handles expired Instagram CDN URLs)
 */
export function getContentThumbnailProxyUrl(contentId: string): string {
  return `${config.apiUrl}/api/content/${contentId}/thumbnail`;
}

/**
 * Hook to fetch content thumbnail via proxy as blob URL (handles auth and CORS)
 */
export function useContentThumbnail(contentId: string, isInstagram: boolean) {
  return useQuery({
    queryKey: ['content-thumbnail', contentId],
    queryFn: async () => {
      const token = await getAuthTokenAsync();
      const response = await fetch(getContentThumbnailProxyUrl(contentId), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        return null;  // UI shows fallback icon for any failure (expired CDN, timeout, etc.)
      }

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    },
    enabled: isInstagram && !!contentId,
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 2 * 60 * 60 * 1000, // 2 hours cache
    retry: false, // Don't retry - 404s won't resolve on retry
  });
}

export function getYouTubeEmbedUrl(youtubeVideoId: string, autoplay = false, start?: number): string {
  const params = new URLSearchParams();
  if (autoplay) params.set('autoplay', '1');
  if (start) params.set('start', start.toString());

  const queryString = params.toString();
  return `https://www.youtube.com/embed/${youtubeVideoId}${queryString ? `?${queryString}` : ''}`;
}
